import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    Box,
    TextField,
    Button,
    IconButton,
    Tooltip,
    TablePagination,
    CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import PageHeader1 from "../../components/common/PageHeader1";
import { fetchWalletBalanceReport } from "../../Redux/Reducers/WalletBalanceReportReducer";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleUnauthorized } from "../../TokenAuth/auth";
import { formatUTCDateForDataGrid, formatUTCDateForExport, convertISTToUTCForAPI } from "../../utils/dateUtils";
import SearchIcon from '@mui/icons-material/Search';
import { BsDownload } from 'react-icons/bs';
import { getIntParam, getStringParam, setIfTruthy } from "../../utils/urlState";

const WalletBalanceReport = () => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const didInitUrlSyncRef = useRef(false);
    const pendingSawLoadingRef = useRef(false);
    const [pendingAction, setPendingAction] = useState(null); // "search" | "date" | null

    const {
        data: walletData = [],
        paginationDetail = { totalRecords: 0, currentPage: 1, perPage: 10 },
        status,
    } = useSelector((state) => state.WalletBalanceReportReducer || {});

    const [searchQuery, setSearchQuery] = useState(getStringParam(searchParams, "search", ""));
    const [appliedSearchQuery, setAppliedSearchQuery] = useState(getStringParam(searchParams, "search", ""));
    const [startDate, setStartDate] = useState(getStringParam(searchParams, "startDate", ""));
    const [appliedStartDate, setAppliedStartDate] = useState(getStringParam(searchParams, "startDate", ""));
    const [endDate, setEndDate] = useState(getStringParam(searchParams, "endDate", ""));
    const [appliedEndDate, setAppliedEndDate] = useState(getStringParam(searchParams, "endDate", ""));
    const [lastAction, setLastAction] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [mobileNumber, setMobileNumber] = useState(null);

    const [paginationModel, setPaginationModel] = useState({
        page: getIntParam(searchParams, "page", 0),
        pageSize: getIntParam(searchParams, "pageSize", 10),
    });

    const redirectProcessed = useRef(false);
    const isInitialMount = useRef(true);
    const isLoading = status === "loading";

    // Restore state from URL on mount (skip if redirect param exists)
    useEffect(() => {
        if (searchParams.get("redirect")) return;
        const modeParam = searchParams.get("mode") || "";
        const searchParam = searchParams.get("search") || "";
        const startDateParam = searchParams.get("startDate") || "";
        const endDateParam = searchParams.get("endDate") || "";

        if (modeParam === "search" || searchParam) {
            if (searchParam) {
                setSearchQuery(searchParam);
                setAppliedSearchQuery(searchParam);
                setLastAction("search");
            }
            return;
        }

        if (modeParam === "date") {
            setStartDate(startDateParam);
            setEndDate(endDateParam);
            setAppliedStartDate(startDateParam);
            setAppliedEndDate(endDateParam);
            setLastAction("date");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Check for redirect parameter on mount and auto-search
    useEffect(() => {
        if (redirectProcessed.current) return;

        const redirectParam = searchParams.get('redirect');
        if (redirectParam) {
            redirectProcessed.current = true;
            setMobileNumber(redirectParam);
            setSearchQuery(redirectParam);
            setAppliedSearchQuery(redirectParam);
            setLastAction("search");
            setPaginationModel(prev => ({ ...prev, page: 0 }));
            // Remove redirect parameter from URL
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('redirect');
            setSearchParams(newSearchParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Disable Search/Apply until request completes.
    useEffect(() => {
        if (!pendingAction) return;
        if (isLoading) {
            pendingSawLoadingRef.current = true;
            return;
        }
        if (!isLoading && pendingSawLoadingRef.current) {
            pendingSawLoadingRef.current = false;
            setPendingAction(null);
        }
    }, [isLoading, pendingAction]);

    // Keep URL in sync so Back restores pagination/filters.
    useEffect(() => {
        if (!didInitUrlSyncRef.current) {
            didInitUrlSyncRef.current = true;
            return;
        }
        const params = new URLSearchParams();
        params.set("page", String(paginationModel.page));
        params.set("pageSize", String(paginationModel.pageSize));

        if (lastAction === "search" && appliedSearchQuery && appliedSearchQuery.trim()) {
            params.set("mode", "search");
            params.set("search", appliedSearchQuery.trim());
        } else if (lastAction === "date") {
            params.set("mode", "date");
            setIfTruthy(params, "startDate", appliedStartDate);
            setIfTruthy(params, "endDate", appliedEndDate);
        }
        setSearchParams(params, { replace: true });
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        lastAction,
        appliedSearchQuery,
        appliedStartDate,
        appliedEndDate,
        setSearchParams,
    ]);

    const handleSearch = () => {
        if (!searchQuery.trim()) {
            setAppliedSearchQuery("");
            setMobileNumber(null);
            setLastAction(null);
            setPaginationModel(prev => ({ ...prev, page: 0 }));
            return;
        }
        setPendingAction("search");
        pendingSawLoadingRef.current = false;
        setAppliedSearchQuery(searchQuery.trim());
        setMobileNumber(null); // Clear mobileNumber when manually searching
        setLastAction("search");
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    const handleApplyFilters = () => {
        setPendingAction("date");
        pendingSawLoadingRef.current = false;
        setAppliedStartDate(startDate || "");
        setAppliedEndDate(endDate || "");
        setLastAction("date");
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    useEffect(() => {
        // Skip initial load if redirect is being processed
        if (redirectProcessed.current && isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        isInitialMount.current = false;

        if (lastAction === "search") {
            if (appliedSearchQuery && appliedSearchQuery.trim()) {
                dispatch(
                    fetchWalletBalanceReport({
                        page: 1,
                        limit: 10,
                        search: mobileNumber ? "" : appliedSearchQuery,
                        startDate: "",
                        endDate: "",
                        mobileNumber: mobileNumber || undefined,
                    })
                );
            } else {
                setLastAction(null);
                setMobileNumber(null);
            }
            return;
        }

        const page = paginationModel.page + 1;
        let limit = Number(paginationModel.pageSize);
        if (isNaN(limit)) {
            limit = 10;
        }

        const params = {
            page: page,
            limit: limit,
        };

        // Only add dates if they are provided
        if (lastAction === "date" && appliedStartDate && appliedEndDate) {
            params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
            params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
            params.search = "";
        } else if (lastAction === null) {
            // Initial load - no dates, no search
            params.search = "";
        }

        dispatch(fetchWalletBalanceReport(params));
    }, [dispatch, paginationModel.page, paginationModel.pageSize, appliedStartDate, appliedEndDate, appliedSearchQuery, lastAction, mobileNumber]);

    const mappedRows = useMemo(() => {
        const pageSize = paginationModel.pageSize;
        return (walletData || []).map((item, index) => ({
            id: item._id || index,
            sono: index + 1 + paginationModel.page * pageSize,
            username: item.username || '-',
            mobileNumber: item.mobileNumber || '-',
            giftAmount: item.giftAmount || 0,
            actualBalance: item.actualBalance || 0,
            totalBalance: item.totalBalance || 0,
            updatedAt: item.updatedAt,
        }));
    }, [walletData, paginationModel]);

    const columns = [
        {
            field: "sono",
            headerName: "S.No",
            width: 80,
            headerAlign: 'center',
            align: 'center',
        },
        { field: "username", headerName: "User Name", width: 200 },
        { field: "mobileNumber", headerName: "Mobile No.", width: 150 },
        {
            field: "giftAmount",
            headerName: "Gift Amount",
            width: 160,
            type: 'number',
            valueFormatter: (value) => `₹${value?.toLocaleString('en-IN') || '0'}`,
        },
        {
            field: "actualBalance",
            headerName: "Actual Balance",
            width: 160,
            type: 'number',
            valueFormatter: (value) => `₹${value?.toLocaleString('en-IN') || '0'}`,
        },
        {
            field: "totalBalance",
            headerName: "Total Balance",
            width: 160,
            type: 'number',
            valueFormatter: (value) => `₹${value?.toLocaleString('en-IN') || '0'}`,
        },
        { field: "updatedAt", headerName: "Updated At", width: 180, valueFormatter: formatUTCDateForDataGrid },
    ];

    const handleChangePage = (event, newPage) =>
        setPaginationModel((prev) => ({ ...prev, page: newPage }));

    const handleChangeRowsPerPage = (event) => {
        const value = event.target.value;
        const newSize = parseInt(value, 10);
        setPaginationModel({ page: 0, pageSize: newSize });
    };

    const handleResetFilter = () => {
        setStartDate("");
        setEndDate("");
        setAppliedStartDate("");
        setAppliedEndDate("");
        setSearchQuery("");
        setAppliedSearchQuery("");
        setMobileNumber(null);
        setLastAction(null);
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    };

    const handleExport = async () => {
        setIsExporting(true);
        const token = localStorage.getItem("astroVani-admin-token");
        try {
            const params = new URLSearchParams();
            if (appliedStartDate) {
                params.append('startDate', convertISTToUTCForAPI(appliedStartDate, 'start'));
            }
            if (appliedEndDate) {
                params.append('endDate', convertISTToUTCForAPI(appliedEndDate, 'end'));
            }
            if (appliedSearchQuery && appliedSearchQuery.trim()) {
                params.append('search', appliedSearchQuery);
            }

            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/walletBalanceReport?${params.toString()}`,
                {
                    headers: { Authorization: token },
                }
            );

            if (response.status === 401) {
                handleUnauthorized();
                throw new Error("Unauthorized: Please login again");
            }

            const allData = response.data?.data || [];

            if (!Array.isArray(allData) || allData.length === 0) {
                toast.warn("No data available to export");
                setIsExporting(false);
                return;
            }

            const exportData = allData.map((row, index) => ({
                "S.No.": index + 1,
                "User Name": row.username || "",
                "Mobile Number": row.mobileNumber || "",
                "Gift Amount": row.giftAmount || 0,
                "Actual Balance": row.actualBalance || 0,
                "Total Balance": row.totalBalance || 0,
                "Updated At": formatUTCDateForExport(row.updatedAt),
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Wallet Balance Report");

            XLSX.writeFile(
                workbook,
                `WalletBalanceReport_${new Date().toISOString().split('T')[0]}.xlsx`
            );
            toast.success("Export successful!");
        } catch (err) {
            console.error("Export error:", err);
            toast.error(err.response?.data?.message || "Failed to export Excel file.");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="body d-flex">
            <div className="container-xxl">
                <ToastContainer />
                <PageHeader1 pagetitle="Wallet Balance Report" />

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        <TextField
                            label="Search..."
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => { if (e.key === 'Enter') { handleSearch(); } }}
                            sx={{ width: 280 }}
                            placeholder="Search by name, email, mobile..."
                        />
                        <IconButton
                            onClick={handleSearch}
                            disabled={isLoading || pendingAction === "search"}
                            sx={{
                                backgroundColor: "var(--primary-color, #1976d2)",
                                color: "#fff",
                                width: 40,
                                height: 40,
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "var(--primary-color, #1976d2)", opacity: 0.9 },
                                "&.Mui-disabled": {
                                    backgroundColor: "rgba(0, 0, 0, 0.12)",
                                    color: "rgba(0, 0, 0, 0.26)",
                                },
                            }}
                        >
                            <SearchIcon />
                        </IconButton>
                    </Box>

                    {/* <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            value={startDate || ""}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                width: { xs: '140px', sm: '160px', md: '180px' },
                                flexShrink: 0,
                                '& .MuiInputBase-input[type="date"]': {
                                    paddingRight: '8px',
                                    paddingLeft: '14px',
                                    position: 'relative',
                                    '&::-webkit-calendar-picker-indicator': {
                                        cursor: 'pointer',
                                        padding: '2px',
                                        marginLeft: '0px',
                                        marginRight: '4px',
                                        opacity: 0.7,
                                        position: 'absolute',
                                        right: 0,
                                        '&:hover': {
                                            opacity: 1,
                                        },
                                    },
                                },
                                '& .MuiOutlinedInput-root': {
                                    paddingRight: '8px',
                                },
                            }}
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            value={endDate || ""}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                width: { xs: '140px', sm: '160px', md: '180px' },
                                flexShrink: 0,
                                '& .MuiInputBase-input[type="date"]': {
                                    paddingRight: '8px',
                                    paddingLeft: '14px',
                                    position: 'relative',
                                    '&::-webkit-calendar-picker-indicator': {
                                        cursor: 'pointer',
                                        padding: '2px',
                                        marginLeft: '0px',
                                        marginRight: '4px',
                                        opacity: 0.7,
                                        position: 'absolute',
                                        right: 0,
                                        '&:hover': {
                                            opacity: 1,
                                        },
                                    },
                                },
                                '& .MuiOutlinedInput-root': {
                                    paddingRight: '8px',
                                },
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleApplyFilters}
                            sx={{
                                borderRadius: "8px",
                                textTransform: "none",
                                backgroundColor: "var(--primary-color, #1976d2)",
                                color: "#fff",
                                height: 40,
                                px: 2,
                                flexShrink: 0,
                                "&:hover": { backgroundColor: "var(--primary-color, #1976d2)", opacity: 0.9 },
                            }}
                        >
                            Apply Filters
                        </Button>
                    </Box> */}

                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                        <Button
                            variant="outlined"
                            onClick={handleResetFilter}
                            sx={{
                                borderRadius: "8px",
                                textTransform: "none",
                                borderColor: "var(--primary-color, #1976d2)",
                                color: "var(--primary-color, #1976d2)",
                                height: 40,
                                px: 2,
                                "&:hover": {
                                    borderColor: "var(--primary-color, #1976d2)",
                                    backgroundColor: "rgba(25, 118, 210, 0.04)",
                                },
                                flexShrink: 0
                            }}
                        >
                            Reset
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleExport}
                            disabled={isExporting}
                            startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <BsDownload />}
                            sx={{
                                borderRadius: "8px",
                                textTransform: "none",
                                backgroundColor: "var(--primary-color, #1976d2)",
                                color: "#fff",
                                fontWeight: 500,
                                height: 40,
                                px: 2,
                                boxShadow: "none",
                                "&:hover": {
                                    backgroundColor: "var(--primary-color, #1976d2)",
                                    boxShadow: "none",
                                },
                                "&:disabled": {
                                    backgroundColor: "rgba(0, 0, 0, 0.12)",
                                    color: "rgba(0, 0, 0, 0.26)",
                                },
                                flexShrink: 0
                            }}
                        >
                            {isExporting ? "Exporting..." : "Export to Excel"}
                        </Button>
                    </Box>
                </Box>

                <div className="row g-0 mb-3">
                    <div className="col-md-12">
                        <div className="card" style={{
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: 'none',
                            overflow: 'hidden'
                        }}>
                            <div className="card-body" style={{ padding: 0 }}>
                                <Box sx={{ display: "flex", justifyContent: "flex-end", backgroundColor: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                                    <TablePagination
                                        component="div"
                                        count={paginationDetail?.totalRecords || 0}
                                        page={paginationModel.page}
                                        onPageChange={handleChangePage}
                                        rowsPerPage={paginationModel.pageSize}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        labelRowsPerPage="Rows per page:"
                                    />
                                </Box>

                                <Box sx={{
                                    width: '100%',
                                    '& .MuiDataGrid-root': {
                                        border: 'none',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        borderBottom: '1px solid #f0f0f0',
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f8f9fa',
                                        borderBottom: '2px solid #e9ecef',
                                        fontWeight: 600,
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        backgroundColor: '#f8f9fa',
                                    },
                                }}>
                                    <DataGrid
                                        autoHeight
                                        rows={mappedRows}
                                        columns={columns}
                                        paginationMode="server"
                                        paginationModel={paginationModel}
                                        onPaginationModelChange={setPaginationModel}
                                        pageSizeOptions={[10, 25, 50, 100]}
                                        loading={status === "loading"}
                                        disableSelectionOnClick
                                        getRowId={(row) => row.id}
                                        hideFooterPagination
                                        hideFooter
                                        sx={{
                                            '& .MuiDataGrid-cell:focus': {
                                                outline: 'none',
                                            },
                                            '& .MuiDataGrid-cell:focus-within': {
                                                outline: 'none',
                                            },
                                        }}
                                    />
                                </Box>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WalletBalanceReport;

